// src/components/RequirementCard.js
import React, { useState } from 'react';
import {formatDate} from '../../formatters/date';
import {
  CheckCircleIcon,
  PencilSquareIcon,
  XCircleIcon,
  TrashIcon,
  EllipsisVerticalIcon,
  ClockIcon,
  DocumentTextIcon,
  FolderOpenIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon as CheckCircleSolid, 
  XCircleIcon as XCircleSolid 
} from '@heroicons/react/24/solid';

const RequirementCard = ({ requirement, onAction, onDelete, onVerCarpeta }) => {
  const [showActions, setShowActions] = useState(false);
  
  const statusConfig = {
    Pendiente: {
      color: 'bg-yellow-100 text-yellow-800',
      icon: <ClockIcon className="h-4 w-4" />,
      label: 'Pendiente'
    },
    Finalizado: {
      color: 'bg-green-100 text-green-800',
      icon: <CheckCircleSolid className="h-4 w-4" />,
      label: 'Finalizado'
    },
    Cancelado: {
      color: 'bg-gray-100 text-gray-800',
      icon: <XCircleIcon className="h-4 w-4" />,
      label: 'Cancelado'
    }
  };
  
  const typeIcons = {
    materiales: <div className="bg-blue-100 p-2 rounded-lg"><DocumentTextIcon className="h-6 w-6 text-blue-600" /></div>,
    expeditor: <div className="bg-purple-100 p-2 rounded-lg"><DocumentTextIcon className="h-6 w-6 text-purple-600" /></div>,
    taller: <div className="bg-yellow-100 p-2 rounded-lg"><DocumentTextIcon className="h-6 w-6 text-yellow-600" /></div>,
    calidad: <div className="bg-green-100 p-2 rounded-lg"><DocumentTextIcon className="h-6 w-6 text-green-600" /></div>,
    personal: <div className="bg-red-100 p-2 rounded-lg"><DocumentTextIcon className="h-6 w-6 text-red-600" /></div>
  };
  
  const status = statusConfig[requirement.estado] || statusConfig.Pendiente;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200">
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 mr-4">
            {typeIcons[requirement.tipo.toString().toLowerCase()] || typeIcons.taller}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between">
              <h3 className="text-lg font-medium text-gray-900 truncate">
                {requirement.numTicket} - {requirement.requerimiento}
              </h3>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                {status.icon}
                <span className="ml-1">{status.label}</span>
              </span>
            </div>
            
            <p className="mt-1 text-gray-600">
            {requirement.observacion} 
            </p>
            
            <div className="mt-2 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                Tipo: {requirement.tipo}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                Creado: {formatDate(requirement.fechaCreacion)}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                Asignado a: {requirement.idAsignado ? requirement.nombreAsignado : 'N/A'}
              </span>
            </div>
          </div>
          
          {/* Menú de acciones para desktop */}
          <div className="hidden md:flex ml-4">
            <div className="flex space-x-1">
              { requirement.idAsignado === null && requirement.estado === 'Pendiente' && (
                <button
                  onClick={() => onAction(requirement, 'approve')}
                  className="inline-flex items-center p-2 rounded-full text-green-600 hover:bg-green-100 focus:outline-none"
                  title="Asignar"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                </button>
              )}
              
              { requirement.estado === 'Pendiente' && (
                <button
                  onClick={() => onAction(requirement, 'edit')}
                  className="inline-flex items-center p-2 rounded-full text-blue-600 hover:bg-blue-100 focus:outline-none"
                  title="Editar"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
              )}
              
              { requirement.estado === 'Pendiente' && (
                <button
                  onClick={() => onAction(requirement, 'cancel')}
                  className="inline-flex items-center p-2 rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none"
                  title="Cancelar"
                >
                  <XCircleIcon className="h-5 w-5" />
                </button>
              )}

              {requirement.idCarpeta && onVerCarpeta && (
                <button
                  onClick={() => onVerCarpeta(requirement.idCarpeta)}
                  className="inline-flex items-center p-2 rounded-full text-indigo-600 hover:bg-indigo-100 focus:outline-none"
                  title="Ver Carpeta"
                >
                  <FolderOpenIcon className="h-5 w-5" />
                </button>
              )}

              { /*(
                <button
                  onClick={() => onDelete(requirement)}
                  className="inline-flex items-center p-2 rounded-full text-red-800 hover:bg-red-100 focus:outline-none"
                  title="Eliminar"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )*/}
            </div>
          </div>
          
          {/* Menú de acciones para móvil */}
          <div className="md:hidden ml-2 relative">
            <button
              onClick={() => setShowActions(!showActions)}
              className="inline-flex items-center p-2 rounded-full text-gray-500 hover:bg-gray-100 focus:outline-none"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>
            
            {showActions && (
              <div className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  {requirement.idAsignado === null && requirement.estado === 'Pendiente' && (
                    <button
                      onClick={() => {
                        onAction(requirement, 'approve');
                        setShowActions(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-green-700 hover:bg-green-100"
                    >
                      <CheckCircleIcon className="mr-2 h-5 w-5" />
                      Asignar
                    </button>
                  )}
                  
                  { requirement.estado === 'Pendiente' && (
                    <button
                      onClick={() => {
                        onAction(requirement, 'edit');
                        setShowActions(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-blue-700 hover:bg-blue-100"
                    >
                      <PencilSquareIcon className="mr-2 h-5 w-5" />
                      Editar
                    </button>
                  )}
                  
                  {requirement.estado === 'Pendiente' && (
                    <button
                      onClick={() => {
                        onAction(requirement, 'cancel');
                        setShowActions(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <XCircleIcon className="mr-2 h-5 w-5" />
                      Cancelar
                    </button>
                  )}
                  
                  {requirement.idCarpeta && onVerCarpeta && (
                    <button
                      onClick={() => { onVerCarpeta(requirement.idCarpeta); setShowActions(false); }}
                      className="flex w-full items-center px-4 py-2 text-sm text-indigo-700 hover:bg-indigo-100"
                    >
                      <FolderOpenIcon className="mr-2 h-5 w-5" />
                      Ver Carpeta
                    </button>
                  )}

                  {/*requirement.estado === 'Pendiente' && (
                    <button
                      onClick={() => {
                        onAction(requirement, 'reject');
                        setShowActions(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-700 hover:bg-red-100"
                    >
                      <XCircleIcon className="mr-2 h-5 w-5" />
                      Rechazar
                    </button>
                  )*/}
                  
                  {/* (
                    <button
                      onClick={() => {
                        onDelete(requirement);
                        setShowActions(false);
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-red-900 hover:bg-red-100"
                    >
                      <TrashIcon className="mr-2 h-5 w-5" />
                      Eliminar
                    </button>
                  )*/}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequirementCard;